//React
import { useEffect } from "react";
//Material UI
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
//Assets
import BodyLeftDefault from "../../assets/images/bodyLeftDefault.svg";
import BodyRightDefault from "../../assets/images/BodyRightDefault.svg";
//CSS
import "./Survey.css";

const radioStyle = {
  "& .MuiSvgIcon-root": {
    fontSize: 20,
  },
  color: "black",
  "&.Mui-checked": {
    color: "var(--color-primary)",
  },
};

const SurveyQuestionSection = ({
  title,
  subtitle,
  questionNumber,
  value,
  onChange,
  options,
  showBodyImage,
  bodyImage,
  bodyImageOptions,
  isBothSelected,
}) => {
  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          paddingBottom: "15px",
        }}
      >
        <div
          className={`survey-question-title ${
            questionNumber > 1 ? "survey-question-title-spacing" : ""
          }`}
        >
          {title}
        </div>
        <div className="survey-question-subtitle">{subtitle}</div>
      </div>
      {questionNumber === 3 && (
        <div
          className={`survey-question3-container ${
            !isBothSelected ? "survey-question3-grid" : ""
          }`}
        >
          {options.map((option) => {
            const selectedValues = Array.isArray(
              value?.[`question${questionNumber}`]
            )
              ? value[`question${questionNumber}`]
              : value?.[`question${questionNumber}`]
              ? [value[`question${questionNumber}`]]
              : [];
            const isSelected = selectedValues.includes(option.value);

            const handleClick = () => {
              if (isSelected) {
                const newValues = selectedValues.filter(
                  (val) => val !== option.value
                );
                onChange({
                  ...value,
                  [`question${questionNumber}`]: newValues,
                });
              } else {
                const newValues = [...selectedValues, option.value];
                onChange({
                  ...value,
                  [`question${questionNumber}`]: newValues,
                });
              }
            };

            return (
              <div
                key={option.value}
                className={`survey-radio-label survey-radio-label-blue ${
                  isSelected ? "selected" : ""
                }`}
                onClick={handleClick}
              >
                <span dangerouslySetInnerHTML={{ __html: option.label }} />
              </div>
            );
          })}
        </div>
      )}
      {questionNumber !== 3 && showBodyImage && bodyImage && (
        <div className="survey-body-image-container">
          <div className="survey-body-image">{bodyImage}</div>
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "6px",
            }}
          >
            {bodyImageOptions.map((option) => {
              const selectedValues = Array.isArray(value?.question2)
                ? value.question2
                : value?.question2
                ? [value.question2]
                : [];
              const isSelected = selectedValues.includes(option.value);

              const handleClick = () => {
                if (isSelected) {
                  const newValues = selectedValues.filter(
                    (val) => val !== option.value
                  );
                  onChange({ ...value, question2: newValues });
                } else {
                  const newValues = [...selectedValues, option.value];
                  onChange({ ...value, question2: newValues });
                }
              };

              return (
                <div
                  key={option.value}
                  className={`survey-radio-label survey-radio-label-${
                    option.color
                  } ${isSelected ? "selected" : ""}`}
                  onClick={handleClick}
                >
                  <span dangerouslySetInnerHTML={{ __html: option.label }} />
                </div>
              );
            })}
          </div>
        </div>
      )}
      {questionNumber !== 3 && !bodyImageOptions && (
        <FormControl>
          <RadioGroup
            value={value?.[`question${questionNumber}`] ?? ""}
            onChange={(e) =>
              onChange({
                ...value,
                [`question${questionNumber}`]: e.target.value,
              })
            }
            className="survey-radio-group"
          >
            {options.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio sx={radioStyle} />}
                label={
                  <span dangerouslySetInnerHTML={{ __html: option.label }} />
                }
              />
            ))}
          </RadioGroup>
        </FormControl>
      )}
    </>
  );
};

const Survey = ({
  setProgressWidth,
  leftShoulder,
  rightShoulder,
  leftSurvey,
  rightSurvey,
  setLeftSurvey,
  setRightSurvey,
  onNext,
  onBack,
}) => {
  const question1Options = [
    { value: "1", label: "전혀 아프지 않다." },
    { value: "2", label: "<strong>뻐근한</strong> 정도 통증" },
    { value: "3", label: "<strong>일상생활에 지장을 받을</strong> 정도 통증" },
    { value: "4", label: "<strong>잠을 못잘 정도</strong> 통증" },
  ];

  const question2Options = [
    { value: "1", label: "① 어깨", color: "red" },
    { value: "2", label: "② 팔뚝", color: "blue" },
    {
      value: "3",
      label: "③ 목 · 승모근 · 견갑골 안쪽의 아픔<br/> 손 · 팔의 저림",
      color: "yellow",
    },
  ];

  const question3Options = [
    { value: "1", label: "가만히 있어도 아프다" },
    { value: "2", label: "특정 자세나 동작을 할 때만 아프다" },
    { value: "3", label: "어깨를 움직일 때 아프다" },
    { value: "4", label: "목을 움직일 때 아프다" },
  ];

  const handleNext = () => {
    onNext();
  };

  // 왼쪽과 오른쪽이 모두 완료되었는지 확인하고 progressWidth 업데이트
  useEffect(() => {
    const leftComplete = leftShoulder ? isLeftComplete() : true;
    const rightComplete = rightShoulder ? isRightComplete() : true;

    if (leftComplete && rightComplete) {
      setProgressWidth(66);
    } else {
      setProgressWidth(33);
    }
  }, [leftShoulder, rightShoulder, leftSurvey, rightSurvey, setProgressWidth]);

  const isBothSelected = leftShoulder && rightShoulder;

  // 선택된 어깨 기준으로 모든 질문이 선택되었는지 확인 (다음 버튼 활성화용)
  const isAllComplete = () => {
    const leftComplete = leftShoulder ? isLeftComplete() : true;
    const rightComplete = rightShoulder ? isRightComplete() : true;
    return leftComplete && rightComplete;
  };

  // 왼쪽 어깨의 모든 질문이 선택되었는지 확인
  const isLeftComplete = () => {
    if (!leftSurvey) return false;
    const q1 = leftSurvey.question1;
    const q2 = leftSurvey.question2;
    const q3 = leftSurvey.question3;

    const hasQ1 = Array.isArray(q1) ? q1.length > 0 : !!q1;
    const hasQ2 = Array.isArray(q2) ? q2.length > 0 : !!q2;
    const hasQ3 = Array.isArray(q3) ? q3.length > 0 : !!q3;

    return hasQ1 && hasQ2 && hasQ3;
  };

  // 오른쪽 어깨의 모든 질문이 선택되었는지 확인
  const isRightComplete = () => {
    if (!rightSurvey) return false;
    const q1 = rightSurvey.question1;
    const q2 = rightSurvey.question2;
    const q3 = rightSurvey.question3;

    const hasQ1 = Array.isArray(q1) ? q1.length > 0 : !!q1;
    const hasQ2 = Array.isArray(q2) ? q2.length > 0 : !!q2;
    const hasQ3 = Array.isArray(q3) ? q3.length > 0 : !!q3;

    return hasQ1 && hasQ2 && hasQ3;
  };

  return (
    <div className="survey-wrapper">
      <div
        className={`survey-containers ${
          isBothSelected ? "survey-containers-row" : ""
        }`}
      >
        {leftShoulder && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              width: "100%",
              height: "100%",
            }}
          >
            <div
              className={`survey-header ${
                isLeftComplete() ? "survey-header-complete" : ""
              }`}
            >
              Left
            </div>
            <div className="survey-container">
              <div className="survey-content">
                <SurveyQuestionSection
                  title="1. 얼마나 아프십니까?"
                  subtitle="통증 강도"
                  questionNumber={1}
                  value={leftSurvey}
                  onChange={setLeftSurvey}
                  options={question1Options}
                />
                <SurveyQuestionSection
                  title="2. 어느 부위가 아프십니까?"
                  subtitle="통증 부위"
                  questionNumber={2}
                  value={leftSurvey}
                  onChange={setLeftSurvey}
                  showBodyImage={true}
                  bodyImage={<BodyLeftDefault />}
                  bodyImageOptions={question2Options}
                />
                <SurveyQuestionSection
                  title="3. 어떤 동작을 할 때 아프십니까?"
                  subtitle="통증 유발 동작"
                  questionNumber={3}
                  value={leftSurvey}
                  onChange={setLeftSurvey}
                  options={question3Options}
                  isBothSelected={isBothSelected}
                />
              </div>
            </div>
          </div>
        )}
        {rightShoulder && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              width: "100%",
              height: "100%",
            }}
          >
            <div
              className={`survey-header ${
                isRightComplete() ? "survey-header-complete" : ""
              }`}
            >
              Right
            </div>
            <div className="survey-container">
              <div className="survey-content">
                <SurveyQuestionSection
                  title="1. 얼마나 아프십니까?"
                  subtitle="통증 강도"
                  questionNumber={1}
                  value={rightSurvey}
                  onChange={setRightSurvey}
                  options={question1Options}
                />
                <SurveyQuestionSection
                  title="2. 어느 부위가 아프십니까?"
                  subtitle="통증 부위"
                  questionNumber={2}
                  value={rightSurvey}
                  onChange={setRightSurvey}
                  showBodyImage={true}
                  bodyImage={<BodyRightDefault />}
                  bodyImageOptions={question2Options}
                />
                <SurveyQuestionSection
                  title="3. 어떤 동작을 할 때 아프십니까?"
                  subtitle="통증 유발 동작"
                  questionNumber={3}
                  value={rightSurvey}
                  onChange={setRightSurvey}
                  options={question3Options}
                  isBothSelected={isBothSelected}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="survey-button-container">
        <button className="survey-button-back" onClick={onBack}>
          뒤로가기
        </button>
        <button
          className={`survey-button ${!isAllComplete() ? "survey-button-disabled" : ""}`}
          onClick={handleNext}
          disabled={!isAllComplete()}
        >
          다음
        </button>
      </div>
    </div>
  );
};

export default Survey;
